import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import BottomNav from './components/BottomNav';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Checkout from './pages/Checkout';
import VirtualTryOn from './pages/VirtualTryOn';
import SizeGuide from './pages/SizeGuide';
import Orders from './pages/Orders';
import AdminPanel from './pages/AdminPanel';
import HomePreview from './pages/HomePreview';
import About from './pages/About';
import JewelryGuide from './pages/JewelryGuide';
import Policies from './pages/Policies';
import AuthCallback from './pages/AuthCallback';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppContent = () => {
  const { user } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (user && !location.hash?.includes('session_id=')) {
      fetchCounts();
    }
  }, [user]);

  // CRITICAL: Detect Google OAuth return synchronously after hooks.
  // This prevents race conditions with /auth/me firing before the session is established.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const fetchCounts = async () => {
    try {
      const [cartRes, wishlistRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cart`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/wishlist`, { withCredentials: true }),
      ]);

      setCartCount(cartRes.data?.items?.length || 0);
      setWishlistCount(wishlistRes.data?.items?.length || 0);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  return (
    <div className="App">
      <Header cartCount={cartCount} wishlistCount={wishlistCount} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/preview" element={<HomePreview />} />
        <Route path="/products" element={<Products />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/virtual-tryon" element={<VirtualTryOn />} />
        <Route path="/size-guide" element={<SizeGuide />} />
        <Route path="/about" element={<About />} />
        <Route
          path="/cart"
          element={
            <ProtectedRoute>
              <Cart />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wishlist"
          element={
            <ProtectedRoute>
              <Wishlist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route path="/jewelry-guide" element={<JewelryGuide />} />
        <Route path="/policies" element={<Policies />} />
      </Routes>
      <Footer />
      <WhatsAppButton />
      <BottomNav cartCount={cartCount} wishlistCount={wishlistCount} />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#FDFBF7',
            color: '#2D3B26',
            border: '1px solid rgba(45, 59, 38, 0.1)',
            borderRadius: '16px',
            fontFamily: 'Outfit, sans-serif',
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;